import LoadingState from "../ui/LoadingState";

const EVENT_CFG = {
  journal:     { icon: "📝", label: "Memory",       accent: "#43A047", bg: "#E8F5E9" },
  milestone:   { icon: "🎯", label: "Milestone",    accent: "#FFA726", bg: "#FFF8E1" },
  growth:      { icon: "📏", label: "Growth Check", accent: "#1E88E5", bg: "#E3F2FD" },
  vaccination: { icon: "💉", label: "Vaccination",  accent: "#8E24AA", bg: "#F3E5F5" },
  foodIntro:   { icon: "🍎", label: "New Food",     accent: "#E65100", bg: "#FFF3E0" },
};

const EventContent = ({ event }) => {
  switch (event._type) {
    case "journal":
      return event.text
        ? <p style={{ margin: 0, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{event.text}</p>
        : null;

    case "milestone":
      return (
        <>
          <p style={{ margin: 0, fontWeight: 600 }}>{event.milestone}</p>
          {event.notes && (
            <p style={{ margin: "0.2rem 0 0", fontSize: "0.85rem", color: "var(--text-light)" }}>
              {event.notes}
            </p>
          )}
          {event.category && (
            <span style={{
              display: "inline-block", marginTop: "0.35rem",
              fontSize: "0.72rem", fontWeight: 600,
              background: "#FFF8E1", color: "#E65100",
              padding: "0.1rem 0.5rem", borderRadius: "50px",
              border: "1px solid #FFE082",
            }}>
              {event.category}
            </span>
          )}
        </>
      );

    case "growth": {
      const parts = [];
      if (event.weight) parts.push(`${event.weight} kg`);
      if (event.height) parts.push(`${event.height} cm`);
      if (event.headCircumference) parts.push(`HC ${event.headCircumference} cm`);
      return parts.length > 0
        ? <p style={{ margin: 0, fontWeight: 600, letterSpacing: "0.3px" }}>{parts.join("  ·  ")}</p>
        : null;
    }

    case "vaccination":
      return (
        <>
          <p style={{ margin: 0, fontWeight: 600 }}>{event.vaccineName}</p>
          {event.dose && (
            <p style={{ margin: "0.2rem 0 0", fontSize: "0.85rem", color: "var(--text-light)" }}>
              Dose: {event.dose}
            </p>
          )}
        </>
      );

    case "foodIntro":
      return (
        <>
          <p style={{ margin: 0, fontWeight: 600 }}>{event.foodItem}</p>
          {event.reaction && (
            <p style={{ margin: "0.2rem 0 0", fontSize: "0.85rem", color: "var(--text-light)" }}>
              Reaction: {event.reaction}
            </p>
          )}
        </>
      );

    default:
      return null;
  }
};

const EventCard = ({ event, isLast }) => {
  const cfg  = EVENT_CFG[event._type] || EVENT_CFG.journal;
  const time = new Date(event.date || event.createdAt).toLocaleTimeString("en-GB", {
    hour: "2-digit", minute: "2-digit",
  });

  return (
    <div style={{ display: "flex", gap: "0.75rem", marginBottom: "0.5rem" }}>
      {/* Timeline column */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
        <div style={{
          width: "34px", height: "34px", borderRadius: "50%",
          background: cfg.bg,
          border: `2px solid ${cfg.accent}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "0.95rem", flexShrink: 0,
        }}>
          {cfg.icon}
        </div>
        {!isLast && (
          <div style={{ width: "2px", flex: 1, minHeight: "16px", background: "var(--border-light)", marginTop: "3px" }} />
        )}
      </div>

      {/* Card */}
      <div style={{
        flex: 1, minWidth: 0, marginBottom: isLast ? "0" : "0.25rem",
        background: "var(--bg-white)",
        borderRadius: "10px",
        border: "1px solid var(--border-light)",
        borderLeft: `3px solid ${cfg.accent}`,
        padding: "0.65rem 0.9rem",
      }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: "0.35rem", gap: "0.5rem",
        }}>
          <span style={{
            fontSize: "0.7rem", fontWeight: 700,
            color: cfg.accent, textTransform: "uppercase", letterSpacing: "0.6px",
          }}>
            {cfg.label}
          </span>
          <span style={{ fontSize: "0.72rem", color: "var(--text-light)", whiteSpace: "nowrap" }}>
            {time}
          </span>
        </div>

        <div style={{ fontSize: "0.9rem", color: "var(--text-dark)" }}>
          <EventContent event={event} />
        </div>

        {event.photoURL && (
          <img
            src={event.photoURL}
            alt="Memory"
            style={{
              width: "100%", maxHeight: "320px",
              objectFit: "cover", borderRadius: "8px",
              marginTop: "0.65rem",
            }}
          />
        )}
      </div>
    </div>
  );
};

const DateSeparator = ({ label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", margin: "1.25rem 0 0.75rem" }}>
    <div style={{ flex: 1, height: "1px", background: "var(--border-light)" }} />
    <span style={{
      fontSize: "0.72rem", fontWeight: 700, color: "var(--text-light)",
      textTransform: "uppercase", letterSpacing: "0.6px", whiteSpace: "nowrap",
    }}>
      {label}
    </span>
    <div style={{ flex: 1, height: "1px", background: "var(--border-light)" }} />
  </div>
);

const JournalFeed = ({ events, loading, emptyMessage = "No entries yet." }) => {
  if (loading) return <LoadingState message="Loading journal…" />;

  if (!events?.length) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">📖</div>
        <p>{emptyMessage}</p>
      </div>
    );
  }

  // Group events by calendar day
  const groups = new Map();
  events.forEach((event) => {
    const d   = new Date(event.date || event.createdAt || 0);
    const key = d.toDateString();
    if (!groups.has(key)) groups.set(key, { date: d, events: [] });
    groups.get(key).events.push(event);
  });

  // Sort days newest-first
  const sortedDays = [...groups.entries()].sort(
    ([, a], [, b]) => b.date - a.date
  );

  const today     = new Date().toDateString();
  const yesterday = (() => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toDateString(); })();

  return (
    <div>
      {sortedDays.map(([key, { date, events: dayEvents }]) => {
        const label =
          key === today     ? "Today" :
          key === yesterday ? "Yesterday" :
          date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

        return (
          <div key={key}>
            <DateSeparator label={label} />
            {dayEvents.map((event, i) => (
              <EventCard
                key={event.id}
                event={event}
                isLast={i === dayEvents.length - 1}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default JournalFeed;
